import {
	CurrencyType,
	KidsPolicyType,
	QuestionType,
	type KidsPolicyItemInput,
	type PlanInput,
} from "@/lib/gql/__generated__/graphql";

import type { ProductDayTripPlanFormModel } from "../../../../type";

type Props = {
	viewModel: ProductDayTripPlanFormModel["ViewModel"];
};

export const viewModelToPlanInputConverter = ({ viewModel }: Props): PlanInput => {
	/**
	 * @todo Generator の型生成に不備があるので明示的に構造を指定する必要がある
	 */
	const coSleepingKidsPrice =
		viewModel.coSleepingKidsPrice && "value" in viewModel.coSleepingKidsPrice
			? viewModel.coSleepingKidsPrice.value !== undefined && viewModel.coSleepingKidsPrice.value !== null
				? {
						amount: viewModel.coSleepingKidsPrice.amount?.value ?? 0,
						currencyType: viewModel.coSleepingKidsPrice.currencyType?.value ?? CurrencyType.Jpy,
					}
				: undefined
			: viewModel.coSleepingKidsPrice
				? {
						amount: viewModel.coSleepingKidsPrice.amount?.value ?? 0,
						currencyType: viewModel.coSleepingKidsPrice.currencyType?.value ?? CurrencyType.Jpy,
					}
				: undefined;

	const availableOptionIDs = viewModel.availableOptionIDs.value.map((item) => item.value);

	const planInput: PlanInput = {
		hotelID: viewModel.hotelID,
		planID: viewModel.planID,

		name: viewModel.name.value,

		availableDiscountIDs: viewModel.availableDiscountIDs.value.map((item) => item.value),
		availableOptionIDs,
		availableRoomIDs: viewModel.availableRoomIDs.value.map((item) => item.value),

		billingTypeTarget: viewModel.billingTypeTarget.value,
		billingTypeTerm: viewModel.billingTypeTerm.value,

		cancelPolicy: viewModel.cancelPolicy?.value?.map((item) => {
			return {
				beforeDays: item.beforeDays?.value ?? 0,
				chargeRatio: item.chargeRatio?.value ?? 0,
			};
		}),

		checkInAvailableDayOfTheWeek: viewModel.checkInAvailableDayOfTheWeek?.value?.map((item) => item.value),

		checkInEnd: viewModel.checkInEnd.value,
		checkInStart: viewModel.checkInStart.value,
		checkOut: viewModel.checkOut.value,

		coSleepingKidsPrice,

		defaultDiscountID: viewModel.defaultDiscountID?.value,

		doNotCountKidsAsStock: viewModel.doNotCountKidsAsStock.value,

		emailTemplateReservation: viewModel.emailTemplateReservation
			? {
					atCreated: viewModel.emailTemplateReservation.atCreated?.value,
					atUpdated: viewModel.emailTemplateReservation.atUpdated?.value,
					atCin: viewModel.emailTemplateReservation.atCin?.value,
					atCanceled: viewModel.emailTemplateReservation.atCanceled?.value,
				}
			: undefined,

		inSale: viewModel.inSale.value,
		isChangeOnlyOption: viewModel.isChangeOnlyOption.value,
		isCoSleepingKidsPriceActive: viewModel.isCoSleepingKidsPriceActive.value,
		includedMeals: viewModel.includedMeals?.value?.map((item) => item.value) ?? [],
		isDayTrip: viewModel.isDayTrip.value,
		isHiddenHotelQuestions: viewModel.isHiddenHotelQuestions.value,
		isNoAddressNeeded: viewModel.isNoAddressNeeded.value,
		isPrivate: viewModel.isPrivate.value,
		isRequest: viewModel.isRequest.value,
		isRoomPriceFree: viewModel.isRoomPriceFree.value,
		isSCConnected: viewModel.isSCConnected?.value,
		isShowRoomForGuest: viewModel.isShowRoomForGuest.value,
		isUncancelable: viewModel.isUncancelable.value,
		isUnchangeable: viewModel.isUnchangeable.value,

		mainKeyVisual: {
			bucket: viewModel.mainKeyVisual.bucket?.value ?? "",
			fileName: viewModel.mainKeyVisual.fileName?.value,
			key: viewModel.mainKeyVisual.key?.value ?? "",
			mimeType: viewModel.mainKeyVisual.mimeType?.value,
			region: viewModel.mainKeyVisual.region?.value ?? "",
		},
		keyVisuals: viewModel.keyVisuals.value.map((item) => {
			return {
				bucket: item.bucket?.value ?? "",
				fileName: item.fileName?.value,
				key: item.key?.value ?? "",
				mimeType: item.mimeType?.value,
				region: item.region?.value ?? "",
			};
		}),

		kidsPolicy: viewModel.kidsPolicy?.value?.map((item) => {
			return {
				kidsDiscountType: item.kidsDiscountType?.value ?? KidsPolicyType.Static,
				kidsMaxAge: item.kidsMaxAge?.value ?? 0,
				ratio: item.ratio?.value,
				static: item.static
					? {
							amount: item.static.amount?.value ?? 0,
							currencyType: item.static.currencyType?.value ?? CurrencyType.Jpy,
						}
					: undefined,
			} satisfies KidsPolicyItemInput;
		}),

		mainTarget: viewModel.mainTarget?.value,

		maxDailyStockNum: viewModel.maxDailyStockNum?.value === 0 ? undefined : viewModel.maxDailyStockNum?.value,
		maxPeopleNumPerRoom: viewModel.maxPeopleNumPerRoom?.value,
		maxRoomNum: viewModel.maxRoomNum?.value,
		maxStockNum: viewModel.maxStockNum?.value,
		minRoomNum: viewModel.minRoomNum?.value,
		minimumPeopleNumPerRoom: viewModel.minimumPeopleNumPerRoom?.value,

		notSendCinMail: viewModel.notSendCinMail.value,
		onlyUseCreditPayment: viewModel.onlyUseCreditPayment.value,
		onlyUseLocalPayment: viewModel.onlyUseLocalPayment.value,

		price: {
			amount: viewModel.price.amount?.value ?? 0,
			currencyType: viewModel.price.currencyType?.value ?? CurrencyType.Jpy,
		},

		questions: viewModel.questions?.value?.map((item) => {
			return {
				questionID: item.questionID?.value ?? "",
				questionType: item.questionType?.value ?? QuestionType.Free,
				required: item.required?.value ?? false,

				question: item.question?.value ?? "",
				options: item.options?.value?.map((item) => item.value),
			};
		}),

		requiredOptionIDs: viewModel.requiredOptionIDs.value.map((item) => item.value),

		// 選択必須グループのオプションも選択可能なオプションである必要があるので、選択できないものは除外する
		requiredOptionGroupIDs: viewModel.requiredOptionGroupIDs.value
			.map((item) => item.value)
			.filter((item) => availableOptionIDs.includes(item)),

		requiredPrepMillisecond: viewModel.requiredPrepMillisecond.value,

		roomChangeDisable: viewModel.roomChangeDisable.value,

		saleEnd: viewModel.saleEnd?.value || undefined,
		saleEndTime: viewModel.saleEndTime?.value || undefined,
		saleStart: viewModel.saleStart?.value || undefined,
		saleStartTime: viewModel.saleStartTime?.value || undefined,
		saleStartBeforeDate: viewModel.saleStartBeforeDate?.value,
		saleTypeForTerm: viewModel.saleTypeForTerm?.value,

		longStayParams: {
			minimumStayDates: viewModel.longStayParams?.minimumStayDates?.value ?? 0,
		},

		shortStayParams: {
			maximumStayDates: viewModel.shortStayParams?.maximumStayDates?.value ?? 1,
		},

		staticPriceParams: viewModel.staticPriceParams?.staticRoomPrices?.value
			? {
					staticRoomPrices: viewModel.staticPriceParams.staticRoomPrices.value.map((item) => {
						return {
							prices: item.prices?.value?.map((item) => {
								return {
									adultNum: item.adultNum?.value ?? 0,
									price: {
										amount: item.price?.amount?.value ?? 0,
										currencyType: item.price?.currencyType?.value ?? CurrencyType.Jpy,
									},
								};
							}),
							roomID: item.roomID?.value ?? "",
							roomName: item.roomName?.value ?? "",
						};
					}),
				}
			: undefined,

		stayableDayOfTheWeek: viewModel.stayableDayOfTheWeek?.value?.map((item) => item.value),
		isHolidayStayable: viewModel.isHolidayStayable?.value ?? undefined,
		isHolidayCheckInAvailable: viewModel.isHolidayCheckInAvailable?.value ?? undefined,
		stayableEnd: viewModel.stayableEnd?.value || undefined,
		stayableStart: viewModel.stayableStart?.value || undefined,
		stockTypeTarget: viewModel.stockTypeTarget?.value,
		stockTypeTerm: viewModel.stockTypeTerm?.value,
	} satisfies PlanInput;

	return planInput;
};
